import type {
  DatadogRum,
  RumEvent,
  RumInitConfiguration,
} from '@datadog/browser-rum';
import type { SafeClientErrorContext } from './reportClientError';
import {
  redactClientErrorString,
  reportClientError,
  sanitizeClientErrorUrl,
  setClientErrorReporter,
} from './reportClientError';

interface ClientMonitoringConfig {
  applicationId: string;
  clientToken: string;
  site: string;
  service: string;
  env: string;
  version: string;
  sessionSampleRate: number;
  sessionReplaySampleRate: number;
  excludedActivityUrls: NonNullable<
    RumInitConfiguration['excludedActivityUrls']
  >;
}

type MutableRumEvent = RumEvent & {
  action?: {
    target?: {
      name?: string;
    };
  };
  context?: Record<string, unknown>;
  error?: {
    message?: string;
    source?: string;
    stack?: string;
  };
  resource?: {
    url?: string;
  };
  view?: {
    referrer?: string;
    url?: string;
  };
};

const DEFAULT_DATADOG_SITE = 'datadoghq.com';
const DEFAULT_DATADOG_SERVICE = 'descope-flows';
const DEFAULT_SESSION_SAMPLE_RATE = 100;
const DEFAULT_SESSION_REPLAY_SAMPLE_RATE = 0;
const DEFAULT_EXCLUDED_ACTIVITY_URLS: NonNullable<
  RumInitConfiguration['excludedActivityUrls']
> = [
  /^https:\/\/api2\.amplitude\.com\/2\/httpapi(?:[?#]|$)/,
  /^https:\/\/featureassets\.org\/v1\/initialize(?:[?#]|$)/,
  /^https:\/\/www\.googletagmanager\.com\/gtm\.js(?:[?#]|$)/,
];
const STATSIG_NETWORK_ERROR_PATTERN = /\[Statsig\].*networking error occurred/i;
const RESIZE_OBSERVER_LOOP_ERROR_PATTERN =
  /ResizeObserver loop (?:completed with undelivered notifications|limit exceeded)/i;
const GENERIC_CROSS_ORIGIN_SCRIPT_ERROR_MESSAGES = new Set([
  'Script error.',
  'Uncaught "Script error."',
  "Uncaught 'Script error.'",
  'Uncaught browser error Script error.',
]);
const WEBKIT_MESSAGE_HANDLERS_ERROR_PATTERN =
  /window\.webkit\.messageHandlers/i;
const CIRCULAR_REACT_DOM_NODE_ERROR_PATTERN =
  /Converting circular structure to JSON[\s\S]*starting at object with constructor 'HTML[A-Za-z]*Element'[\s\S]*__reactFiber\$[\s\S]*stateNode' closes the circle/i;
const ANONYMOUS_APPEND_CHILD_JSON_STACK_PATTERN =
  /at JSON\.stringify @ <anonymous>[\s\S]*at HTML[A-Za-z]*Element\.appendChild @ <anonymous>:\d+:\d+/i;
const EXPECTED_DESCOPE_ERROR_PATTERNS = [
  /Illegal email address/i,
  /Failed to verify email code/i,
  /Let's double-check your (email address|details|password|username)/i,
  /Max attempts exceeded for one time code/i,
  /OAuth cancell?ed by user/i,
  /One time code expired/i,
  /One time code is invalid/i,
  /(?:Password signin failed|Invalid signin credentials|User not found) Wrong password or unknown user/i,
  /That email doesn't match/i,
  /That username is already taken/i,
  /This email is already in use/i,
  /We couldn't validate this email address/i,
];
const DESCOPE_RESULT_FAIL_ERROR_PATTERN =
  /^\[Descope\]\s+"?map\[[\s\S]*\bresult:fail\b[\s\S]*\]"?(?:\s+Error:?)?\s*$/i;
const DESCOPE_UI_LOAD_FAILURE_MESSAGE = 'DescopeUI was not loaded';
const DESCOPE_UI_SCRIPT_ID_PREFIX = 'npmlib-descopeweb-components-ui-';
const DESCOPE_UI_SCRIPT_SELECTOR = `script[id^="${DESCOPE_UI_SCRIPT_ID_PREFIX}"], script[src*="/npm/@descope/web-components-ui@"]`;
const DESCOPE_UI_RESOURCE_PATH_PATTERN = /\/npm\/@descope\/web-components-ui@/;

let hasInitializedClientMonitoring = false;
let removeGlobalErrorHandlers: (() => void) | undefined;
let clientTimingReporter: Pick<DatadogRum, 'addTiming'> | undefined;
let pendingClientTimings: Array<{ name: string; time: number }> = [];

type DescopeResourceTiming = PerformanceResourceTiming & {
  responseStatus?: number;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getStringField = (
  value: Record<string, unknown>,
  key: string
): string | undefined =>
  typeof value[key] === 'string' ? (value[key] as string) : undefined;

const getOptionalEnv = (key: keyof ImportMetaEnv): string | undefined => {
  const value = import.meta.env[key];
  return value === '' ? undefined : value;
};

const getConfiguredActivityUrlExclusions = (): NonNullable<
  RumInitConfiguration['excludedActivityUrls']
> =>
  [
    getOptionalEnv('VITE_BROWSER_FINGERPRINTING_JS_URL'),
    getOptionalEnv('VITE_CEP_JS_URL'),
  ].reduce<NonNullable<RumInitConfiguration['excludedActivityUrls']>>(
    (excludedActivityUrls, url) =>
      url ? [...excludedActivityUrls, url] : excludedActivityUrls,
    DEFAULT_EXCLUDED_ACTIVITY_URLS
  );

const parseSampleRate = (
  value: string | undefined,
  fallback: number
): number => {
  if (value === undefined) {
    return fallback;
  }

  const parsedValue = Number(value);
  if (!Number.isFinite(parsedValue)) {
    return fallback;
  }

  return Math.min(100, Math.max(0, parsedValue));
};

const isAutomaticErrorEvent = (source: string | undefined): boolean =>
  source === 'console' || source === 'source';

const isExpectedDescopeValidationError = (message: string): boolean =>
  message.includes('[Descope]') &&
  (DESCOPE_RESULT_FAIL_ERROR_PATTERN.test(message) ||
    EXPECTED_DESCOPE_ERROR_PATTERNS.some(pattern => pattern.test(message)));

const getErrorValueText = (
  value: unknown
): { message: string; stack: string } => {
  if (value instanceof Error) {
    return {
      message: value.message,
      stack: value.stack ?? '',
    };
  }

  if (typeof value === 'string') {
    return { message: value, stack: '' };
  }

  if (isRecord(value)) {
    return {
      message:
        getStringField(value, 'message') ??
        getStringField(value, 'error') ??
        getStringField(value, 'detail') ??
        '',
      stack: getStringField(value, 'stack') ?? '',
    };
  }

  return { message: '', stack: '' };
};

const isDescopeUiLoadFailure = (
  value: unknown,
  fallbackMessage = ''
): boolean => {
  const { message, stack } = getErrorValueText(value);

  return [message, stack, fallbackMessage].some(text =>
    text.includes(DESCOPE_UI_LOAD_FAILURE_MESSAGE)
  );
};

const getHostFromUrl = (value: string): string | undefined => {
  try {
    const baseUrl =
      typeof window !== 'undefined'
        ? window.location.href
        : 'https://linktr.ee';

    return new URL(value, baseUrl).host || undefined;
  } catch {
    return undefined;
  }
};

const getFiniteNumber = (value: unknown): number | undefined =>
  typeof value === 'number' && Number.isFinite(value)
    ? Math.round(value)
    : undefined;

const getNonEmptyString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.length > 0 ? value : undefined;

const getDescopeUiScriptElements = (): HTMLScriptElement[] => {
  if (typeof document === 'undefined') {
    return [];
  }

  try {
    return Array.from(
      document.querySelectorAll<HTMLScriptElement>(DESCOPE_UI_SCRIPT_SELECTOR)
    );
  } catch {
    return [];
  }
};

const getDescopeUiScriptStatus = (
  script: HTMLScriptElement | undefined
): string => {
  if (!script) {
    return 'missing';
  }

  return script.getAttribute('status') ?? (script.src ? 'present' : 'no-src');
};

const getLatestDescopeUiResourceTiming = ():
  DescopeResourceTiming | undefined => {
  if (
    typeof performance === 'undefined' ||
    typeof performance.getEntriesByType !== 'function'
  ) {
    return undefined;
  }

  try {
    return performance
      .getEntriesByType('resource')
      .reduce<DescopeResourceTiming | undefined>((latestEntry, entry) => {
        const resourceEntry = entry as DescopeResourceTiming;

        if (!DESCOPE_UI_RESOURCE_PATH_PATTERN.test(resourceEntry.name)) {
          return latestEntry;
        }

        if (!latestEntry || resourceEntry.startTime >= latestEntry.startTime) {
          return resourceEntry;
        }

        return latestEntry;
      }, undefined);
  } catch {
    return undefined;
  }
};

const getDescopeUiLoadDiagnostics = (): SafeClientErrorContext => {
  const scripts = getDescopeUiScriptElements();
  const script = scripts[scripts.length - 1];
  const resourceTiming = getLatestDescopeUiResourceTiming();

  return {
    diagnostic: 'descope-ui-load-failure',
    descopeUiGlobalPresent:
      typeof (globalThis as { DescopeUI?: unknown }).DescopeUI !== 'undefined',
    descopeUiReplaceAllSupported: 'replaceAll' in String.prototype,
    descopeUiScriptCount: scripts.length,
    descopeUiScriptCrossOrigin: getNonEmptyString(script?.crossOrigin),
    descopeUiScriptHost: script?.src ? getHostFromUrl(script.src) : undefined,
    descopeUiScriptIntegrityPresent: script ? Boolean(script.integrity) : false,
    descopeUiScriptStatus: getDescopeUiScriptStatus(script),
    descopeUiResourceCompressedBytes: getFiniteNumber(
      resourceTiming?.encodedBodySize
    ),
    descopeUiResourceDurationMs: getFiniteNumber(resourceTiming?.duration),
    descopeUiResourceUncompressedBytes: getFiniteNumber(
      resourceTiming?.decodedBodySize
    ),
    descopeUiResourceHost: resourceTiming?.name
      ? getHostFromUrl(resourceTiming.name)
      : undefined,
    descopeUiResourceInitiatorType: getNonEmptyString(
      resourceTiming?.initiatorType
    ),
    descopeUiResourceResponseStatus: getFiniteNumber(
      resourceTiming?.responseStatus
    ),
    descopeUiResourceTransferSize: getFiniteNumber(
      resourceTiming?.transferSize
    ),
  };
};

const getClientErrorContext = (
  source: string,
  value: unknown,
  fallbackMessage = ''
): SafeClientErrorContext => ({
  source,
  ...(isDescopeUiLoadFailure(value, fallbackMessage)
    ? getDescopeUiLoadDiagnostics()
    : {}),
});

const isInjectedWebKitBridgeError = (message: string, stack = ''): boolean => {
  // Instagram/Facebook iOS in-app browsers inject a native bridge script on
  // pagehide. When the WebKit handler is missing it throws from the document
  // URL, even though the script is not served by this app.
  return (
    WEBKIT_MESSAGE_HANDLERS_ERROR_PATTERN.test(message) ||
    WEBKIT_MESSAGE_HANDLERS_ERROR_PATTERN.test(stack)
  );
};

const isInjectedWebKitBridgeErrorValue = (
  value: unknown,
  fallbackMessage = ''
): boolean => {
  const { message, stack } = getErrorValueText(value);
  return isInjectedWebKitBridgeError(message || fallbackMessage, stack);
};

const isInjectedAppendChildCircularJsonError = (
  message: string,
  stack = ''
): boolean => {
  return (
    CIRCULAR_REACT_DOM_NODE_ERROR_PATTERN.test(message) &&
    ANONYMOUS_APPEND_CHILD_JSON_STACK_PATTERN.test(stack)
  );
};

const isInjectedAppendChildCircularJsonErrorValue = (
  value: unknown,
  fallbackMessage = ''
): boolean => {
  const { message, stack } = getErrorValueText(value);
  return isInjectedAppendChildCircularJsonError(
    message || fallbackMessage,
    stack
  );
};

const isGenericCrossOriginScriptError = (
  message: string,
  stack = ''
): boolean => {
  const normalizedMessage = message.trim();

  if (!GENERIC_CROSS_ORIGIN_SCRIPT_ERROR_MESSAGES.has(normalizedMessage)) {
    return false;
  }

  if (normalizedMessage === 'Uncaught browser error Script error.') {
    return true;
  }

  const normalizedStack = stack.trim();

  return (
    normalizedStack.length === 0 ||
    normalizedStack === 'Error: Script error.' ||
    /(?:^|\n)\s*at undefined @\s*$/i.test(normalizedStack)
  );
};

const isGenericCrossOriginScriptErrorValue = (
  value: unknown,
  fallbackMessage = ''
): boolean => {
  const { message, stack } = getErrorValueText(value);
  return isGenericCrossOriginScriptError(message || fallbackMessage, stack);
};

const isResizeObserverLoopError = (message: string, stack = ''): boolean =>
  RESIZE_OBSERVER_LOOP_ERROR_PATTERN.test(message) ||
  RESIZE_OBSERVER_LOOP_ERROR_PATTERN.test(stack);

const isResizeObserverLoopErrorValue = (
  value: unknown,
  fallbackMessage = ''
): boolean => {
  const { message, stack } = getErrorValueText(value);
  return isResizeObserverLoopError(message || fallbackMessage, stack);
};

const shouldDropRumEvent = (event: MutableRumEvent): boolean => {
  const message = event.error?.message ?? '';
  const stack = event.error?.stack ?? '';
  const source = event.error?.source;

  if (
    isInjectedWebKitBridgeError(message, stack) ||
    isInjectedAppendChildCircularJsonError(message, stack) ||
    isGenericCrossOriginScriptError(message, stack) ||
    isResizeObserverLoopError(message, stack)
  ) {
    return true;
  }

  if (!isAutomaticErrorEvent(source)) {
    return false;
  }

  return (
    STATSIG_NETWORK_ERROR_PATTERN.test(message) ||
    isExpectedDescopeValidationError(message)
  );
};

export const sanitizeRumEvent = (event: RumEvent): boolean => {
  const mutableEvent = event as MutableRumEvent;

  if (shouldDropRumEvent(mutableEvent)) {
    return false;
  }

  if (
    isDescopeUiLoadFailure({
      message: mutableEvent.error?.message,
      stack: mutableEvent.error?.stack,
    })
  ) {
    mutableEvent.context = {
      ...mutableEvent.context,
      ...getDescopeUiLoadDiagnostics(),
    };
  }

  if (mutableEvent.view?.url) {
    mutableEvent.view.url = sanitizeClientErrorUrl(mutableEvent.view.url);
  }

  if (mutableEvent.view?.referrer) {
    mutableEvent.view.referrer = sanitizeClientErrorUrl(
      mutableEvent.view.referrer
    );
  }

  if (mutableEvent.resource?.url) {
    mutableEvent.resource.url = sanitizeClientErrorUrl(
      mutableEvent.resource.url
    );
  }

  if (mutableEvent.error?.message) {
    mutableEvent.error.message = redactClientErrorString(
      mutableEvent.error.message
    );
  }

  if (mutableEvent.error?.stack) {
    mutableEvent.error.stack = redactClientErrorString(
      mutableEvent.error.stack
    );
  }

  if (mutableEvent.action?.target?.name) {
    mutableEvent.action.target.name = '[REDACTED_ACTION]';
  }

  return true;
};

export const getClientMonitoringConfig = ():
  ClientMonitoringConfig | undefined => {
  if (getOptionalEnv('VITE_DATADOG_RUM_ENABLED') === 'false') {
    return undefined;
  }

  const applicationId = getOptionalEnv('VITE_DATADOG_RUM_APPLICATION_ID');
  const clientToken = getOptionalEnv('VITE_DATADOG_CLIENT_TOKEN');

  if (!applicationId || !clientToken) {
    return undefined;
  }

  if (
    import.meta.env.DEV &&
    getOptionalEnv('VITE_DATADOG_RUM_ENABLED') !== 'true'
  ) {
    return undefined;
  }

  return {
    applicationId,
    clientToken,
    site: getOptionalEnv('VITE_DATADOG_SITE') ?? DEFAULT_DATADOG_SITE,
    service: getOptionalEnv('VITE_DATADOG_SERVICE') ?? DEFAULT_DATADOG_SERVICE,
    env:
      getOptionalEnv('VITE_DATADOG_ENV') ??
      getOptionalEnv('VITE_STATSIG_STAGE') ??
      import.meta.env.MODE,
    version: getOptionalEnv('VITE_BUILD_VERSION') ?? 'local',
    sessionSampleRate: parseSampleRate(
      getOptionalEnv('VITE_DATADOG_RUM_SESSION_SAMPLE_RATE'),
      DEFAULT_SESSION_SAMPLE_RATE
    ),
    sessionReplaySampleRate: parseSampleRate(
      getOptionalEnv('VITE_DATADOG_RUM_REPLAY_SAMPLE_RATE'),
      DEFAULT_SESSION_REPLAY_SAMPLE_RATE
    ),
    excludedActivityUrls: getConfiguredActivityUrlExclusions(),
  };
};

export const initializeClientMonitoring = async (): Promise<boolean> => {
  if (hasInitializedClientMonitoring) {
    return true;
  }

  const config = getClientMonitoringConfig();
  if (!config) {
    return false;
  }

  try {
    const { datadogRum } = await import('@datadog/browser-rum');
    const initConfig: RumInitConfiguration = {
      applicationId: config.applicationId,
      clientToken: config.clientToken,
      site: config.site,
      service: config.service,
      env: config.env,
      version: config.version,
      sessionSampleRate: config.sessionSampleRate,
      sessionReplaySampleRate: config.sessionReplaySampleRate,
      trackUserInteractions: false,
      trackResources: true,
      trackLongTasks: true,
      excludedActivityUrls: config.excludedActivityUrls,
      defaultPrivacyLevel: 'mask-user-input',
      beforeSend: sanitizeRumEvent,
      silentMultipleInit: true,
    };

    datadogRum.init(initConfig);
    setClientErrorReporter(datadogRum as Pick<DatadogRum, 'addError'>);
    clientTimingReporter = datadogRum;
    pendingClientTimings.forEach(({ name, time }) => {
      datadogRum.addTiming(name, time);
    });
    pendingClientTimings = [];

    if (config.sessionReplaySampleRate > 0) {
      datadogRum.startSessionReplayRecording();
    }

    hasInitializedClientMonitoring = true;
    return true;
  } catch (error) {
    reportClientError('Datadog RUM failed to initialise', error, {
      source: 'clientMonitoring',
    });
    return false;
  }
};

export const addClientTiming = (
  name: string,
  time = performance.now()
): void => {
  if (clientTimingReporter) {
    clientTimingReporter.addTiming(name, time);
    return;
  }

  pendingClientTimings.push({ name, time });
};

export const registerGlobalClientErrorHandlers = (): (() => void) => {
  if (removeGlobalErrorHandlers) {
    return removeGlobalErrorHandlers;
  }

  const handleError = (event: ErrorEvent) => {
    if (
      isInjectedWebKitBridgeErrorValue(event.error, event.message) ||
      isInjectedAppendChildCircularJsonErrorValue(event.error, event.message) ||
      isGenericCrossOriginScriptErrorValue(event.error, event.message) ||
      isResizeObserverLoopErrorValue(event.error, event.message)
    ) {
      return;
    }

    reportClientError('Uncaught browser error', event.error ?? event.message, {
      ...getClientErrorContext('window.onerror', event.error, event.message),
    });
  };

  const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    reportClientError('Unhandled promise rejection', event.reason, {
      ...getClientErrorContext('unhandledrejection', event.reason),
    });
  };

  window.addEventListener('error', handleError);
  window.addEventListener('unhandledrejection', handleUnhandledRejection);

  removeGlobalErrorHandlers = () => {
    window.removeEventListener('error', handleError);
    window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    removeGlobalErrorHandlers = undefined;
  };

  return removeGlobalErrorHandlers;
};

export const resetClientMonitoringForTests = (): void => {
  hasInitializedClientMonitoring = false;
  clientTimingReporter = undefined;
  pendingClientTimings = [];
  removeGlobalErrorHandlers?.();
};
